<template>
  <div class="contact-page">
    <!-- Header Section -->
    <section class="contact-header">
      <div class="container">
        <div class="header-content animate__animated animate__fadeInUp">
          <h1>Get in Touch</h1>
          <p>Ready to transform your development workflow? Let's discuss your needs and get you started.</p>
          <div class="contact-highlights">
            <div class="highlight-item">
              <i class="fas fa-rocket"/>
              <span>14-day free trial</span>
            </div>
            <div class="highlight-item">
              <i class="fas fa-headset"/>
              <span>24/7 enterprise support</span>
            </div>
            <div class="highlight-item">
              <i class="fas fa-clock"/>
              <span>Quick setup in minutes</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Options -->
    <section class="contact-options">
      <div class="container">
        <div class="contact-grid">
          <!-- Contact Form -->
          <div class="contact-form-section animate__animated animate__fadeInLeft">
            <div class="form-header">
              <h2>Send us a message</h2>
              <p>Fill out the form below and we'll get back to you within 24 hours</p>
            </div>

            <form @submit.prevent="submitForm" class="contact-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="firstName">First Name *</label>
                  <input
                    id="firstName"
                    v-model="form.firstName"
                    type="text"
                    required
                    :disabled="submitting"
                    placeholder="John"
                  >
                </div>
                <div class="form-group">
                  <label for="lastName">Last Name *</label>
                  <input
                    id="lastName"
                    v-model="form.lastName"
                    type="text"
                    required
                    :disabled="submitting"
                    placeholder="Doe"
                  >
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="email">Email Address *</label>
                  <input
                    id="email"
                    v-model="form.email"
                    type="email"
                    required
                    :disabled="submitting"
                    placeholder="john@company.com"
                  >
                </div>
                <div class="form-group">
                  <label for="company">Company</label>
                  <input
                    id="company"
                    v-model="form.company"
                    type="text"
                    :disabled="submitting"
                    placeholder="Your Company"
                  >
                </div>
              </div>

              <div class="form-group">
                <label for="phone">Phone Number</label>
                <input
                  id="phone"
                  v-model="form.phone"
                  type="tel"
                  :disabled="submitting"
                  placeholder="+1 (555) 123-4567"
                >
              </div>

              <div class="form-group">
                <label for="inquiryType">Inquiry Type *</label>
                <select id="inquiryType"
v-model="form.inquiryType"
required
:disabled="submitting">
                  <option value="">Select inquiry type</option>
                  <option value="trial">Start Free Trial</option>
                  <option value="demo">Schedule Demo</option>
                  <option value="enterprise">Enterprise Solution</option>
                  <option value="self-managed">{{ $t('contactForm.topicSelfManaged') }}</option>
                  <option value="walkthrough">{{ $t('contactForm.topicWalkthrough') }}</option>
                  <option value="support">Technical Support</option>
                  <option value="partnership">Partnership</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div class="form-group">
                <label for="teamSize">Team Size</label>
                <select id="teamSize" v-model="form.teamSize" :disabled="submitting">
                  <option value="">Select team size</option>
                  <option value="1-5">1-5 developers</option>
                  <option value="6-20">6-20 developers</option>
                  <option value="21-50">21-50 developers</option>
                  <option value="51-100">51-100 developers</option>
                  <option value="100+">100+ developers</option>
                </select>
              </div>

              <div class="form-group">
                <label for="message">Message *</label>
                <textarea
                  id="message"
                  v-model="form.message"
                  required
                  :disabled="submitting"
                  rows="4"
                  placeholder="Tell us about your development needs, compliance requirements, or any specific questions you have..."
                />
              </div>

              <div class="form-group checkbox-group">
                <label class="checkbox-label">
                  <input
                    v-model="form.newsletter"
                    type="checkbox"
                    :disabled="submitting"
                  >
                  <span class="checkmark"/>
                  Subscribe to our newsletter for platform updates and development insights
                </label>
              </div>

              <button type="submit" class="btn btn-primary submit-btn" :disabled="submitting">
                <i v-if="submitting" class="fas fa-spinner fa-spin"/>
                <i v-else class="fas fa-paper-plane"/>
                {{ submitting ? 'Sending...' : 'Send Message' }}
              </button>
            </form>
          </div>

          <!-- Contact Information -->
          <div class="contact-info-section animate__animated animate__fadeInRight">
            <div class="contact-methods">
              <h3>Other ways to reach us</h3>

              <div class="contact-method">
                <div class="method-icon">
                  <i class="fas fa-envelope"/>
                </div>
                <div class="method-content">
                  <h4>Email</h4>
                  <p>contact@caucasus.systems</p>
                  <span class="response-time">Response within 24 hours</span>
                </div>
              </div>

              <div class="contact-method">
                <div class="method-icon">
                  <i class="fas fa-calendar"/>
                </div>
                <div class="method-content">
                  <h4>Schedule a Demo</h4>
                  <p>Book a 30-minute technical demonstration</p>
                  <button class="btn btn-outline" @click="openCalendar">
                    <i class="fas fa-external-link-alt"/>
                    Book Demo Call
                  </button>
                </div>
              </div>

              <div class="contact-method">
                <div class="method-icon">
                  <i class="fas fa-phone"/>
                </div>
                <div class="method-content">
                  <h4>Enterprise Sales</h4>
                  <p>Direct line for enterprise inquiries</p>
                  <span class="response-time">Available 24/7</span>
                </div>
              </div>
            </div>

            <!-- Office Information -->
            <div class="office-info">
              <h3>Our Location</h3>
              <div class="office-card">
                <i class="fas fa-map-marker-alt"/>
                <div>
                  <h4>Caucasus Region Office</h4>
                  <p>Serving clients across the Caucasus region and beyond</p>
                  <p class="timezone">GMT+4 Timezone</p>
                </div>
              </div>
            </div>

            <!-- Response Times -->
            <div class="response-info">
              <h3>Response Times</h3>
              <div class="response-grid">
                <div class="response-item">
                  <i class="fas fa-bolt"/>
                  <div>
                    <strong>General Inquiries</strong>
                    <span>Within 24 hours</span>
                  </div>
                </div>
                <div class="response-item">
                  <i class="fas fa-rocket"/>
                  <div>
                    <strong>Enterprise Sales</strong>
                    <span>Within 4 hours</span>
                  </div>
                </div>
                <div class="response-item">
                  <i class="fas fa-headset"/>
                  <div>
                    <strong>Technical Support</strong>
                    <span>Within 2 hours</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!--
      Legal & company information — added for Paddle merchant review. This
      block is a discoverable contact path that maps to the same data
      Paddle reviewers look for (entity name, jurisdiction, role-specific
      emails, links to legal pages). It augments the existing form and
      "other ways to reach us" section above; it does NOT replace them.

      TODO(ops): if a registered business address is on file for
      Caucasus Systems LLC, add it to the .legal-block__addr <p> below
      before submitting to Paddle. Until then the block lists only the
      country, which is acceptable for the application but not ideal.
    -->
    <section class="contact-legal">
      <div class="container">
        <div class="legal-block">
          <div class="legal-block__head">
            <span class="legal-block__eyebrow">Legal &amp; company information</span>
            <h2>Caucasus Systems LLC</h2>
            <p class="legal-block__addr">
              Registered in <strong>Georgia</strong> (the country).
              <span class="legal-block__todo">
                TODO(ops): add registered business address before Paddle
                submission.
              </span>
            </p>
          </div>

          <div class="legal-block__grid">
            <div class="legal-block__col">
              <h3>Talk to us</h3>
              <ul class="legal-block__list">
                <li>
                  <span class="legal-block__role">Support &amp; general</span>
                  <a href="mailto:contact@caucasus.systems">contact@caucasus.systems</a>
                </li>
                <li>
                  <span class="legal-block__role">Legal &amp; DPA</span>
                  <a href="mailto:legal@caucasus.systems">legal@caucasus.systems</a>
                </li>
                <li>
                  <span class="legal-block__role">Abuse reports</span>
                  <a href="mailto:abuse@caucasus.systems">abuse@caucasus.systems</a>
                </li>
                <li>
                  <span class="legal-block__role">Security disclosure</span>
                  <a href="mailto:security@caucasus.systems">security@caucasus.systems</a>
                </li>
              </ul>
            </div>

            <div class="legal-block__col">
              <h3>Policies &amp; documents</h3>
              <ul class="legal-block__list legal-block__list--links">
                <li><router-link to="/terms">Terms of Service</router-link></li>
                <li><router-link to="/privacy">Privacy Policy</router-link></li>
                <li><router-link to="/refunds">Refund Policy</router-link></li>
                <li><router-link to="/aup">Acceptable Use Policy</router-link></li>
                <li><router-link to="/dpa">Data Processing Addendum</router-link></li>
                <li><router-link to="/subprocessors">Subprocessors</router-link></li>
                <li><router-link to="/security">Security overview</router-link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="contact-faq">
      <div class="container">
        <div class="section-header animate__animated animate__fadeInUp">
          <h2>Common Questions</h2>
          <p>Quick answers to frequently asked questions</p>
        </div>

        <div class="faq-grid">
          <div
            v-for="(faq, index) in contactFaqs"
            :key="index"
            class="faq-item animate__animated animate__fadeInUp"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="faq-question" @click="toggleFaq(index)">
              <h4>{{ faq.question }}</h4>
              <i class="fas fa-chevron-down" :class="{ 'rotated': faq.open }"/>
            </div>
            <div class="faq-answer" v-show="faq.open">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import api from '@/services/api'

// Door CTAs land here with ?topic=self-managed (door E "Talk to us") or
// ?topic=walkthrough (door D secondary). The topic pre-selects the inquiry
// type so the enquiry is routed without the visitor re-stating it.
const TOPIC_TO_INQUIRY = { 'self-managed': 'self-managed', walkthrough: 'walkthrough' }

export default {
  name: 'Contact',
  data() {
    return {
      submitting: false,
      form: {
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        inquiryType: '',
        teamSize: '',
        message: '',
        newsletter: false
      },
      contactFaqs: [
        {
          question: 'How quickly can I get started?',
          answer: 'You can start a free trial immediately after submitting the contact form. Development environments are typically provisioned within 2 minutes.',
          open: false
        },
        {
          question: 'Do you offer custom enterprise solutions?',
          answer: 'Yes, we provide fully customized solutions including dedicated infrastructure, specialized compliance, and tailored SLAs for enterprise clients.',
          open: false
        },
        {
          question: 'What compliance standards do you support?',
          answer: 'We support SOC 2, GDPR, HIPAA, and government-specific compliance requirements. Our infrastructure is designed for the most stringent security standards.',
          open: false
        },
        {
          question: 'Can you integrate with our existing tools?',
          answer: 'Absolutely. We provide extensive integrations with popular development tools, CI/CD pipelines, and enterprise systems through our API and custom connectors.',
          open: false
        },
        {
          question: 'What kind of support do you provide?',
          answer: 'We offer 24/7 enterprise support with dedicated technical account managers, comprehensive documentation, and live chat support for all plans.',
          open: false
        }
      ]
    }
  },
  mounted() {
    const topic = String(this.$route?.query?.topic || '')
    if (TOPIC_TO_INQUIRY[topic] && !this.form.inquiryType) {
      this.form.inquiryType = TOPIC_TO_INQUIRY[topic]
    }
  },
  methods: {
    async submitForm() {
      if (this.submitting) return

      // Basic validation
      if (!this.form.firstName || !this.form.lastName || !this.form.email || !this.form.inquiryType || !this.form.message) {
        this.$store.dispatch('showNotification', {
          type: 'error',
          message: 'Please fill in all required fields'
        })
        return
      }

      this.submitting = true

      try {
        // Prepare form data
        const formData = {
          ...this.form,
          timestamp: new Date().toISOString(),
          source: 'website_contact_form',
          userAgent: navigator.userAgent
        }

        // Submit to API
        await api.submitContactForm(formData)

        // Show success message
        this.$store.dispatch('showNotification', {
          type: 'success',
          message: 'Thank you for contacting us! We\'ll get back to you within 24 hours.'
        })

        // Reset form
        this.resetForm()

        // Log contact form submission
        this.$store.dispatch('logSecurityEvent', {
          type: 'contact_form_submission',
          inquiry_type: this.form.inquiryType,
          company: this.form.company
        })
      } catch (error) {
        console.error('Contact form submission failed:', error)

        this.$store.dispatch('showNotification', {
          type: 'error',
          message: 'Failed to send message. Please try again or contact us directly at contact@caucasus.systems'
        })
      } finally {
        this.submitting = false
      }
    },

    resetForm() {
      this.form = {
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        inquiryType: '',
        teamSize: '',
        message: '',
        newsletter: false
      }
    },

    toggleFaq(index) {
      this.contactFaqs[index].open = !this.contactFaqs[index].open
    },

    openCalendar() {
      // In a real implementation, this would open a calendar booking system
      this.$store.dispatch('showNotification', {
        type: 'info',
        message: 'Calendar booking system will open here. For now, please use the contact form to schedule a demo.'
      })
    }
  }
}
</script>

<style scoped>
.contact-page {
  min-height: 100vh;
  background: #f9fafb;
  padding-top: 80px;
}

/* Header Section */
.contact-header {
  padding: 80px 0;
  text-align: center;
  color: white;
  background: #0a2540;
  background-image: url('/media/recraft/textures/texture-grid.svg');
  background-size: cover;
  background-position: center;
  position: relative;
}

.contact-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 37, 64, 0.9);
  z-index: 0;
}

.contact-header .container {
  position: relative;
  z-index: 1;
}

.contact-header h1 {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.contact-header p {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.contact-highlights {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.highlight-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.highlight-item i {
  color: #10b981;
}

/* Contact Options */
.contact-options {
  padding: 80px 0;
  background: white;
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: flex-start;
}

/* Contact Form */
.form-header {
  margin-bottom: 2rem;
}

.form-header h2 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #333;
}

.form-header p {
  color: #666;
  line-height: 1.5;
}

.contact-form {
  display: grid;
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #1a7fd4;
  box-shadow: 0 0 0 3px rgba(26, 127, 212, 0.1);
}

.form-group input:disabled,
.form-group select:disabled,
.form-group textarea:disabled {
  background: #f8fafc;
  cursor: not-allowed;
  opacity: 0.7;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.checkbox-group {
  flex-direction: row;
  align-items: flex-start;
  gap: 0.75rem;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  line-height: 1.5;
}

.checkbox-label input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkmark {
  width: 20px;
  height: 20px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 4px;
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
  margin-top: 2px;
}

.checkbox-label input:checked + .checkmark {
  background: #1a7fd4;
  border-color: #1a7fd4;
}

.checkbox-label input:checked + .checkmark::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.submit-btn {
  padding: 1rem 2rem;
  font-size: 1.1rem;
  margin-top: 1rem;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Contact Information */
.contact-info-section {
  display: grid;
  gap: 3rem;
}

.contact-methods h3,
.office-info h3,
.response-info h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #333;
}

.contact-method {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 15px;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.contact-method:hover {
  background: #e2e8f0;
  transform: translateX(5px);
}

.method-icon {
  width: 50px;
  height: 50px;
  background: #0a2540;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.method-content h4 {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
}

.method-content p {
  color: #666;
  margin-bottom: 0.5rem;
}

.response-time {
  font-size: 0.9rem;
  color: #51cf66;
  font-weight: 500;
}

.office-card {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #0a2540;
  color: white;
  border-radius: 15px;
}

.office-card i {
  font-size: 1.5rem;
  color: #1a7fd4;
  margin-top: 0.25rem;
}

.office-card h4 {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.office-card p {
  margin-bottom: 0.25rem;
  opacity: 0.9;
}

.timezone {
  font-size: 0.9rem;
  opacity: 0.8;
}

.response-grid {
  display: grid;
  gap: 1rem;
}

.response-item {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 10px;
}

.response-item i {
  color: #1a7fd4;
  font-size: 1.2rem;
}

.response-item strong {
  display: block;
  font-weight: 600;
  color: #333;
}

.response-item span {
  color: #666;
  font-size: 0.9rem;
}

/* FAQ Section */
.contact-faq {
  padding: 80px 0;
  background: #f8fafc;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #0a2540;
}

.section-header p {
  color: #666;
  font-size: 1.1rem;
}

.faq-grid {
  display: grid;
  gap: 1rem;
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  overflow: hidden;
}

.faq-question {
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.faq-question:hover {
  background: #f8fafc;
}

.faq-question h4 {
  font-weight: 600;
  margin: 0;
  color: #333;
}

.faq-question i {
  transition: transform 0.3s ease;
  color: #1a7fd4;
}

.faq-question i.rotated {
  transform: rotate(180deg);
}

.faq-answer {
  padding: 0 1.5rem 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.faq-answer p {
  margin: 1rem 0 0 0;
  line-height: 1.6;
  color: #666;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .contact-grid {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
}

@media (max-width: 768px) {
  .contact-header h1 {
    font-size: 2.5rem;
  }

  .contact-highlights {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .contact-method {
    flex-direction: column;
    text-align: center;
  }

  .office-card {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .contact-header {
    padding: 60px 0;
  }

  .contact-header h1 {
    font-size: 2rem;
  }

  .contact-options {
    padding: 60px 0;
  }

  .form-header h2 {
    font-size: 1.5rem;
  }
}

/* ── Legal & company information block (Paddle-review surface) ─────── */
.contact-legal {
  background: #f6f1e5;
  padding: 80px 0;
  border-top: 1px solid #e0d6bf;
}

.legal-block {
  max-width: 980px;
  margin: 0 auto;
  background: #fbf7ee;
  border: 1px solid #d8ccaf;
  border-left: 4px solid #7a1b25;
  border-radius: 6px;
  padding: 2.5rem;
  position: relative;
}

.legal-block::before {
  content: '§';
  position: absolute;
  top: 1rem;
  right: 1.25rem;
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.8rem;
  color: #b88a2c;
  line-height: 1;
}

.legal-block__head {
  border-bottom: 1px solid #e0d6bf;
  padding-bottom: 1.25rem;
  margin-bottom: 1.75rem;
}

.legal-block__eyebrow {
  display: block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: #44506a;
  margin-bottom: 0.6rem;
}

.legal-block__head h2 {
  font-family: 'Fraunces', Georgia, serif;
  font-weight: 500;
  font-style: italic;
  font-size: 2.1rem;
  color: #1b2233;
  margin: 0 0 0.5rem;
  letter-spacing: -0.005em;
}

.legal-block__addr {
  color: #44506a;
  font-size: 0.98rem;
  margin: 0;
}

.legal-block__addr strong {
  color: #1b2233;
}

.legal-block__todo {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.82rem;
  color: #7a1b25;
  font-style: italic;
  opacity: 0.85;
}

.legal-block__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
}

.legal-block__col h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #1b2233;
  margin: 0 0 1rem;
  font-weight: 600;
}

.legal-block__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.legal-block__list li {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.legal-block__role {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #44506a;
}

.legal-block__list a {
  color: #7a1b25;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-size: 0.98rem;
}

.legal-block__list a:hover {
  color: #1b2233;
}

.legal-block__list--links {
  gap: 0.55rem;
}

.legal-block__list--links li {
  flex-direction: row;
}

@media (max-width: 720px) {
  .legal-block {
    padding: 1.75rem;
  }
  .legal-block__grid {
    grid-template-columns: 1fr;
    gap: 1.75rem;
  }
}
</style>
